import React from 'react';
import PropTypes from 'prop-types';
import { strings } from '../../../../localization';
import lsUtil from '../../../../components/common/lsUtil';
import ProgressOverlay from '../../../../components/common/ProgressOverlay';
import ResizeThrottler from '../../../../components/ResizeThrottler';
import { NOT_APPLICABLE_SCORE, SUPPORTED_TIME_PERIOD_TYPES } from '../SafetyScore';
import { colorForSafetyScore } from '../SafetyScore';
import { MAX_SCORE_TREND_PREVIOUS_PERIODS, timePeriodShortLabelForDate } from '../SafetyScoreModuleGroup';
import {
    Axis,
    Chart,
    Geom,
    Tooltip
} from "bizcharts";

const IDEAL_HEIGHT_FOR_WIDTH_375 = 265; // a magic number determined by comparing the horizontal and vertical dimensions of the chart's labels (horizontal affected by max width of strings, vertical is simply line height)
const IDEAL_TOP_PADDING_FOR_WIDTH_375 = 5;
const IDEAL_RIGHT_PADDING_FOR_WIDTH_375 = 50; // leaves enough room for angled x axis labels
const IDEAL_BOTTOM_PADDING_FOR_WIDTH_375 = 40 + 25; // +25 to align better alongside Score Summary
const IDEAL_LEFT_PADDING_FOR_WIDTH_375 = 30;
const IDEAL_AXIS_LABEL_FONT_SIZE_FOR_WIDTH_375 = 10;

const RESIZE_EVENT_HANDLER_THROTTLE_INTERVAL_MS = 100;

const NOT_APPLICABLE_SCORE_PLACEHOLDER = 0;

export default class ScoreTrend extends React.PureComponent {

    static propTypes = {
        totalTimePeriodsLoaded: PropTypes.number.isRequired,
        selectedTimePeriod: PropTypes.number.isRequired,
        selectedTimePeriodType: PropTypes.string.isRequired,
        selectedScoreThresholds: PropTypes.object.isRequired,
        score: PropTypes.number.isRequired,
        progressPercentageComplete: PropTypes.number, // load progress
        periodStartDate: PropTypes.instanceOf(Date),
        periodEndDate: PropTypes.instanceOf(Date),
        trendScoresSummaries: PropTypes.array
    };

    constructor(props) {

        super(props);

        this.chartContainerRef = React.createRef();

        this.state = {
            parentDivWidth: 0,
            dynamicHeight: 0
        };
    };

    updateChartDimensions = () => {

        if (this.chartContainerRef.current) {

            const parentDivWidth = this.chartContainerRef.current.offsetWidth;
            const dynamicHeight = IDEAL_HEIGHT_FOR_WIDTH_375  * (parentDivWidth / 375);

            if (this.state.parentDivWidth !== parentDivWidth ||
                this.state.dynamicHeight !== dynamicHeight) {
                this.setState({
                    parentDivWidth,
                    dynamicHeight
                });
            }
        }
    };

    componentDidMount() {
        this.updateChartDimensions();
    };

    componentDidUpdate(prevProps) {
        this.updateChartDimensions();
    }

    getColorForScore = (score) => {
        return colorForSafetyScore(score, this.props.selectedScoreThresholds);
    };

    typeColorJSXexpression = ["score", this.getColorForScore];

    scoreValueForChartData = (score) => {
        if (score === NOT_APPLICABLE_SCORE) {
            return NOT_APPLICABLE_SCORE_PLACEHOLDER;
        }
        return Math.round(score * 10) / 10;
    };

    chartData = () => {

        let data = [];

        // Resultant data should be in chronological order.
        // <Array>.push() adds to the end of the array but is more performant than <Array>.unshift() which adds to the beginning of the array
        // Therefore, looping in reverse order so that <Array>.push() will result in the data being in chronological order
        // Then append the current score last.

        if (this.props.trendScoresSummaries && this.props.trendScoresSummaries.length > 0) {

            for (let index = this.props.trendScoresSummaries.length -1; index >= 0; index--) {

                const trendScoreSummary = this.props.trendScoresSummaries[index];
                const trendPeriodStartDateString = timePeriodShortLabelForDate(trendScoreSummary.periodStartDate);
                const trendPeriodEndDateString = timePeriodShortLabelForDate(trendScoreSummary.periodEndDate);

                data.push({
                    period: trendPeriodStartDateString + " - " + trendPeriodEndDateString,
                    score: this.scoreValueForChartData(trendScoreSummary.score),
                    periodindex: index + 1 // + 1 accounts for the current period being 0
                });
            }
        }

        if (this.props.totalTimePeriodsLoaded) { // checks to avoid rendering empty model before data is loaded
            data.push({
                period: timePeriodShortLabelForDate(this.props.periodStartDate) + " - " + strings.score_time_period_present,
                score: this.scoreValueForChartData(this.props.score),
                periodindex: 0
            });
        }
        return data;
    };

    currentIdealWidthConversionFactor = () => {
        return (this.state.parentDivWidth / 375);
    }

    render() {

        let scoreTrendPeriodDescription = "";
        let totalTimePeriods = 1 + MAX_SCORE_TREND_PREVIOUS_PERIODS; // current period + previous periods

        switch (this.props.selectedTimePeriodType) {
            case SUPPORTED_TIME_PERIOD_TYPES.WEEKLY:
                scoreTrendPeriodDescription = strings.formatString(strings.score_trend_last_x_weeks, totalTimePeriods);
                break;
            case SUPPORTED_TIME_PERIOD_TYPES.MONTHLY:
                scoreTrendPeriodDescription = strings.formatString(strings.score_trend_last_x_months, totalTimePeriods);
                break;
            case SUPPORTED_TIME_PERIOD_TYPES.QUARTERLY:
                scoreTrendPeriodDescription = strings.formatString(strings.score_trend_last_x_quarters, totalTimePeriods);
                break;
            default:
                console.warn("ScoreTrend unsupported time period: " + this.props.selectedTimePeriodType);
                break;
        }

        let selectedTheme = lsUtil.getItem('raven.theme') || 'light';
        let currentPeriodPointer = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsSAAALEgHS3X78AAAAkElEQVRIie3VwQ2EIBRF0fsnFkApljClWwKl2MFzgyZGkY+KK0hIYME9K8Ak0XL8mtY70IEOfAMM68LMAjC+1I2SZgAkkd6jAERAD2cEwtZdFy8hu/gBeIgc4qfATeQ0ngUqkWz8EnAil/EiUECKcReQQVxxSZj3T04XcUrb/3aRSudqPv2E4I1XA3dG88duAeh2dtIT5MXmAAAAAElFTkSuQmCC";
        if(selectedTheme === 'dark') {
            currentPeriodPointer = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAk0lEQVRIie3UwRUCIQyE4YnPAkIn9F/FdEI6iCeU9a1sEPHEnDj9X06Iu2PlbkvrG9jABv4D3OsjpaQ55/yLKEmWUgwA4O5wd6iqkqRPjiRVVWv3CQCYRmq8PfwAzCBtvAt8g7zHL4ER5CweAiLIp3gY6CG9+BBwhlzFh4EWicRbQGpcREIIAJiZRQAAL2DVln92DzAS4ZgjGav/AAAAAElFTkSuQmCC"
        }

        const idealWidthConversionFactor = this.currentIdealWidthConversionFactor();
        return (
            <section className="score-trend" ref={this.chartContainerRef} >
                <header className="score-header">
                    <h6>
                        <span>
                            {strings.score_trend_header}
                        </span>
                    </h6>
                    <span>
                        {scoreTrendPeriodDescription}
                    </span>
                </header>
                <div>
                    <Chart
                        width={this.state.parentDivWidth}
                        height={this.state.dynamicHeight}
                        padding={[
                            IDEAL_TOP_PADDING_FOR_WIDTH_375 * idealWidthConversionFactor + 6, // +6 is a magic number for leaving enough room for the periodindex image Geom
                            IDEAL_RIGHT_PADDING_FOR_WIDTH_375 * idealWidthConversionFactor,
                            IDEAL_BOTTOM_PADDING_FOR_WIDTH_375 * idealWidthConversionFactor,
                            IDEAL_LEFT_PADDING_FOR_WIDTH_375 * idealWidthConversionFactor
                        ]}
                        data={this.chartData()}
                        scale={{
                            score: {
                                alias: strings.score_y_axis_label,
                                tickInterval: 25
                            }
                        }}
                        forceFit
                    >
                        <Tooltip
                            showTitle={true}
                            crosshairs={{
                                type: "x"
                            }}
                        />
                        <Axis
                            name="period"
                            label={{
                                textStyle: {
                                    fontSize: IDEAL_AXIS_LABEL_FONT_SIZE_FOR_WIDTH_375 * idealWidthConversionFactor
                                }
                            }}
                        />
                        <Axis
                            name="score"
                            label={{
                                textStyle: {
                                    fontSize: IDEAL_AXIS_LABEL_FONT_SIZE_FOR_WIDTH_375 * idealWidthConversionFactor
                                }
                            }}
                        />
                        <Geom
                            type="interval"
                            position="period*score"
                            color={this.typeColorJSXexpression}
                        />
                        <Geom
                            type="point"
                            position="period*score"
                            size={24}
                            shape={[
                                "periodindex",
                                (periodindex) => {
                                    if (periodindex === this.props.selectedTimePeriod) {
                                        return ["image", currentPeriodPointer];
                                    }
                                    return ["image", "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsSAAALEgHS3X78AAAAJklEQVRIie3NMQEAAAjDMMC/52ECvlRA00nqs3m9AwAAAAAAAJy1C7oDLddyCRYAAAAASUVORK5CYII="]
                                }
                            ]}
                        />
                    </Chart>
                </div>
                {this.props.progressPercentageComplete !== 100 &&
                    <ProgressOverlay />
                }
                <ResizeThrottler interval={RESIZE_EVENT_HANDLER_THROTTLE_INTERVAL_MS} callback={this.updateChartDimensions} />
            </section>
        );
    }
}